import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiErrorDetail } from "@/lib/api";
import { previewText } from "@/components/templates/TemplateFormDialog";
import { Textarea } from "@/components/ui/textarea";
import { smsLength } from "@/components/conversations/SmsComposer";
import { SMS_MAX_CHARS, SUGGESTED_SMS, TRIGGERS, smsPreviewText, smsProblem, triggerOf, unitLabel } from "@/lib/autoMessagesConstants";
import { AUTO_MESSAGES } from "@/constants/testIds";

const SELECT_BASE = "h-10 rounded-md border border-input bg-background px-3 text-sm";
const SELECT_CLASS = `w-full ${SELECT_BASE}`;
const templateKey = (t) => `${t.name}|${t.language}`;

// Fix AN (2026-10-23): crear o editar una regla de mensaje automático.
export default function RuleFormDialog({ open, onOpenChange, rule, services, onSubmit, smsAvailable = false }) {
  const editing = Boolean(rule);
  const [channel, setChannel] = useState("whatsapp"); // Fix AQ (2026-10-23): "whatsapp" (plantilla) o "sms" (texto propio)
  const [smsText, setSmsText] = useState("");
  const [smsLanguage, setSmsLanguage] = useState("es");
  const smsRef = useRef(null);
  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState("before_appointment");
  const [offsetValue, setOffsetValue] = useState("24");
  const [offsetUnit, setOffsetUnit] = useState("hours");
  const [when, setWhen] = useState("before");
  const [template, setTemplate] = useState("");
  const [serviceIds, setServiceIds] = useState([]);
  const [isActive, setIsActive] = useState(false);
  const [templates, setTemplates] = useState({ status: "loading", items: [], error: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const t = triggerOf(rule?.trigger || "before_appointment");
    setName(rule?.name || "");
    setTrigger(t.key);
    setOffsetValue(String(rule ? rule.offset_value : t.defaultValue));
    setOffsetUnit(rule?.offset_unit || t.defaultUnit);
    setWhen(rule?.when || "before");
    setTemplate(rule?.template_name ? `${rule.template_name}|${rule.template_language}` : ""); // Fix AQ (2026-10-23): una regla de SMS no tiene plantilla
    setServiceIds(rule?.service_ids || []);
    setIsActive(Boolean(rule?.is_active));
    setChannel(rule?.channel || "whatsapp");
    setSmsText(rule?.sms_text || "");
    setSmsLanguage(rule?.template_language || "es");
    setSaving(false);
    setTemplates({ status: "loading", items: [], error: "" });
    api.get("/conversations/templates")
      .then(({ data }) => { if (alive) setTemplates({ status: "ready", items: data, error: "" }); })
      .catch((err) => { if (alive) setTemplates({ status: "error", items: [], error: formatApiErrorDetail(err.response?.data?.detail) }); });
    return () => { alive = false; };
  }, [open, rule]);

  const spec = triggerOf(trigger);
  // Solo las plantillas que usan datos que este tipo de mensaje completa solo (y con datos con nombre)
  const usable = useMemo(
    () => templates.items.filter((t) => (t.params.length === 0 || t.named) && t.params.every((p) => spec.params.includes(p))),
    [templates.items, spec],
  );
  const chosen = templates.items.find((t) => templateKey(t) === template) || null;

  const changeTrigger = (key) => {
    const t = triggerOf(key);
    setTrigger(key);
    setOffsetValue(String(t.defaultValue));
    setOffsetUnit(t.defaultUnit);
    // Fix AV (2026-10-27): se mira si la plantilla elegida sirve para el tipo NUEVO (antes se miraba el anterior y quedaba elegida una plantilla que el servidor rechaza).
    setTemplate((prev) => (templates.items.some((u) => templateKey(u) === prev && (u.params.length === 0 || u.named) && u.params.every((p) => t.params.includes(p))) ? prev : ""));
  };

  const toggleService = (id) => setServiceIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  const valueNumber = Number(offsetValue);
  const isSms = channel === "sms";
  const smsIssue = isSms ? smsProblem(smsText, spec.params) : "";
  const channelReady = isSms ? Boolean(smsText.trim()) && !smsIssue : Boolean(template);
  const canSave = !saving && name.trim() && channelReady && offsetValue !== "" && Number.isInteger(valueNumber) && valueNumber >= 0;

  const insertVariable = (variable) => {
    const token = `{${variable}}`;
    const el = smsRef.current;
    const start = el?.selectionStart ?? smsText.length;
    const end = el?.selectionEnd ?? start;
    setSmsText(smsText.slice(0, start) + token + smsText.slice(end));
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(start + token.length, start + token.length); } });
  };

  const submit = async () => {
    const [template_name, template_language] = isSms ? [null, smsLanguage] : template.split("|");
    setSaving(true);
    const ok = await onSubmit({
      name: name.trim(), trigger, offset_value: valueNumber, offset_unit: offsetUnit, when: trigger === "treatment_due" ? when : null,
      template_name, template_language, channel, sms_text: isSms ? smsText.trim() : null, service_ids: serviceIds, is_active: isActive,
    });
    if (!ok) setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={AUTO_MESSAGES.formDialog}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{editing ? "Editar regla" : "Nueva regla"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Nombre</Label>
            <Input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Recordatorio 24 horas antes" data-testid={AUTO_MESSAGES.nameInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Cuándo se manda</Label>
            <select className={SELECT_CLASS} value={trigger} onChange={(e) => changeTrigger(e.target.value)} data-testid={AUTO_MESSAGES.triggerSelect}>
              {TRIGGERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div className="flex gap-2 items-end flex-wrap">
            <div className="w-24">
              <Label className="text-sm text-muted-foreground mb-1 block">Tiempo</Label>
              <Input type="number" min={0} max={365} value={offsetValue} onChange={(e) => setOffsetValue(e.target.value)} data-testid={AUTO_MESSAGES.offsetValueInput} />
            </div>
            <select className={`${SELECT_BASE} w-32`} value={offsetUnit} onChange={(e) => setOffsetUnit(e.target.value)} data-testid={AUTO_MESSAGES.offsetUnitSelect}>
              {spec.units.map((u) => <option key={u} value={u}>{unitLabel(u, 2)}</option>)}
            </select>
            {trigger === "treatment_due" && (
              <select className={`${SELECT_BASE} flex-1 min-w-[10rem]`} value={when} onChange={(e) => setWhen(e.target.value)} data-testid={AUTO_MESSAGES.whenSelect}>
                <option value="before">antes del vencimiento</option>
                <option value="after">después del vencimiento</option>
              </select>
            )}
          </div>
          {trigger === "on_booking" && <p className="text-xs text-muted-foreground -mt-2">Con 0 se manda apenas se reserva (el ciclo corre cada 5 minutos).</p>}
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Se manda por</Label>
            <div className="flex gap-2">
              {[["whatsapp", "WhatsApp", AUTO_MESSAGES.channelWhatsapp], ["sms", "SMS", AUTO_MESSAGES.channelSms]].map(([key, label, testId]) => (
                <button key={key} type="button" aria-pressed={channel === key} onClick={() => setChannel(key)} data-testid={testId}
                  className={`rounded-full border px-4 py-1.5 text-sm [@media(pointer:coarse)]:min-h-[44px] ${channel === key ? "bg-brand text-white border-brand" : "border-border text-muted-foreground hover:bg-muted"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {isSms && (
            <div className="space-y-2">
              {!smsAvailable && (
                <p className="text-xs text-[#B5793A] bg-[#F4E4D6] rounded-lg px-3 py-2" data-testid={AUTO_MESSAGES.smsOffNotice}>Los SMS todavía no están activados (Agente IA &gt; SMS). Podés dejar la regla lista: no manda nada hasta que los actives.</p>
              )}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="text-sm text-muted-foreground">Texto del SMS</Label>
                <div className="flex items-center gap-2">
                  <select className={`${SELECT_BASE} h-8 text-xs`} value={smsLanguage} onChange={(e) => setSmsLanguage(e.target.value)} aria-label="Idioma de los nombres de mes y la hora" data-testid={AUTO_MESSAGES.smsLanguageSelect}>
                    <option value="es">Fecha en español</option>
                    <option value="en">Fecha en inglés</option>
                  </select>
                  <Button type="button" variant="outline" size="sm" className="h-8 [@media(pointer:coarse)]:min-h-[44px]" onClick={() => setSmsText(SUGGESTED_SMS[trigger])} data-testid={AUTO_MESSAGES.smsSuggestionButton}>Usar el texto sugerido</Button>
                </div>
              </div>
              <Textarea ref={smsRef} value={smsText} onChange={(e) => setSmsText(e.target.value)} rows={4} maxLength={SMS_MAX_CHARS} placeholder="Hola {nombre}, te recordamos tu turno..." data-testid={AUTO_MESSAGES.smsTextInput} />
              <div className="flex flex-wrap gap-1.5 items-center">
                <span className="text-xs text-muted-foreground">Datos que se completan solos:</span>
                {spec.params.map((p) => (
                  <button key={p} type="button" onClick={() => insertVariable(p)} data-testid={`${AUTO_MESSAGES.smsVariablePrefix}${p}`}
                    className="text-xs border border-border rounded-full px-2.5 py-1 hover:bg-muted [@media(pointer:coarse)]:min-h-[44px] [@media(pointer:coarse)]:px-3">{`{${p}}`}</button>
                ))}
              </div>
              {smsIssue && <p className="text-xs text-destructive" data-testid={AUTO_MESSAGES.smsProblem}>{smsIssue}</p>}
              {smsText.trim() && !smsIssue && (
                <div>
                  <p className="text-sm whitespace-pre-wrap bg-muted rounded-lg px-3 py-2 break-words" data-testid={AUTO_MESSAGES.smsPreview}>{smsPreviewText(smsText, smsLanguage, trigger)}</p>
                  {(() => {
                    const { chars, parts, unicode } = smsLength(smsPreviewText(smsText, smsLanguage, trigger));
                    return <p className="text-xs text-muted-foreground mt-1" data-testid={AUTO_MESSAGES.smsCounter}>Con datos de ejemplo: {chars} caracteres · {parts} SMS{unicode ? ". Las tildes (á, í, ó, ú) y los emojis acortan cada SMS a 70 caracteres." : "."}</p>;
                  })()}
                </div>
              )}
              {spec.params.includes("instrucciones") && <p className="text-xs text-muted-foreground" data-testid={AUTO_MESSAGES.instructionsHint}>{"{instrucciones}"} se completa con lo que el tratamiento tiene cargado en Agente IA &gt; Base de conocimiento ({trigger === "after_treatment" ? "Despues de la cita" : "Antes de la cita"}). Si un tratamiento no lo tiene, a esa paciente no se le manda y queda anotado en el registro.</p>}
              <p className="text-xs text-muted-foreground">Incluí el nombre de la clínica y cómo darse de baja (STOP): los operadores de EE. UU. lo piden. Sin negritas ni emojis. {(trigger === "on_booking" || trigger === "before_appointment") && "Si el texto invita a contestar CONFIRMO o REAGENDAR, el turno se confirma solo, o el chat pasa al equipo."}</p>
            </div>
          )}
          {!isSms && <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Plantilla de WhatsApp</Label>
            {templates.status === "loading" && <p className="text-sm text-muted-foreground">Cargando plantillas...</p>}
            {templates.status === "error" && <p className="text-sm text-destructive break-words">{templates.error || "No se pudieron cargar las plantillas."}</p>}
            {templates.status === "ready" && (
              <select className={SELECT_CLASS} value={template} onChange={(e) => setTemplate(e.target.value)} data-testid={AUTO_MESSAGES.templateSelect}>
                <option value="">Elegí una plantilla</option>
                {usable.map((t) => <option key={templateKey(t)} value={templateKey(t)}>{t.name.replaceAll("_", " ")} ({t.language})</option>)}
                {chosen && !usable.some((t) => templateKey(t) === template) && <option value={template}>{chosen.name.replaceAll("_", " ")} ({chosen.language})</option>}
              </select>
            )}
            <p className="text-xs text-muted-foreground mt-1">Solo se muestran las aprobadas que usan {spec.params.join(", ")}. ¿No tenés una? Creala en Plantillas con uno de los textos sugeridos.</p>
            {chosen && (
              <div className="mt-2">
                <p className="text-sm whitespace-pre-wrap bg-muted rounded-lg px-3 py-2" data-testid={AUTO_MESSAGES.templatePreview}>{previewText(chosen.body, chosen.footer)}</p>
                {chosen.buttons.length > 0 && <div className="flex flex-wrap gap-1.5 mt-1.5">{chosen.buttons.map((b, i) => <span key={i} className="text-xs border border-border rounded-full px-2.5 py-1">{b}</span>)}</div>}
                {chosen.params.includes("instrucciones") && <p className="text-xs text-muted-foreground mt-1" data-testid={AUTO_MESSAGES.instructionsHint}>{"{{instrucciones}}"} se completa con lo que el tratamiento tiene cargado en Agente IA &gt; Base de conocimiento ({trigger === "after_treatment" ? "Despues de la cita" : "Antes de la cita"}). Si un tratamiento no lo tiene, a esa paciente no se le manda y queda anotado en el registro.</p>}
                {(trigger === "on_booking" || trigger === "before_appointment") && (
                  <p className="text-xs text-muted-foreground mt-1">Si la plantilla tiene un botón que empieza con "Confirmo" y otro como "Necesito reagendar", la paciente confirma o pide un cambio con un toque: el turno se confirma solo, o el chat pasa al equipo.</p>
                )}
              </div>
            )}
          </div>}
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Tratamientos</Label>
            <p className="text-xs text-muted-foreground mb-2">Sin elegir ninguno, la regla vale para todos los tratamientos.</p>
            <div className="bg-white border border-border rounded-xl divide-y divide-border max-h-44 overflow-y-auto">
              {services.length === 0 && <p className="text-sm text-muted-foreground p-3">No hay tratamientos cargados.</p>}
              {services.map((s) => (
                <label key={s.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40">
                  <Checkbox checked={serviceIds.includes(s.id)} onCheckedChange={() => toggleService(s.id)} data-testid={`${AUTO_MESSAGES.serviceCheckboxPrefix}${s.id}`} />
                  <span className="text-sm">{s.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Regla activa</p>
              <p className="text-xs text-muted-foreground">Al activarla, solo se mandan los mensajes que correspondan de ahora en adelante: no se manda nada de turnos o tratamientos anteriores.</p>
            </div>
            <Switch checked={isActive} onCheckedChange={setIsActive} data-testid={AUTO_MESSAGES.activeToggle} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={!canSave} className="bg-brand hover:bg-brand-hover text-white" data-testid={AUTO_MESSAGES.saveButton}>
            {saving ? "Guardando..." : "Guardar regla"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
